import { Component } from '@theme/component';

/** Native playback and scrolling remain available when enhancement is unavailable. */
class EchtCommunityReels extends Component {
  #abort;
  #visibility;

  #states = new Map();
  #motion;
  #rail;
  #navigation;
  #previous;
  #next;
  #resize;
  #nearby;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#bindNavigation(signal);
    this.querySelectorAll('video').forEach((video) => this.#bindVideo(video, signal));
    this.#nearby?.disconnect();
    this.#nearby = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const video = entry.target.querySelector('video');
        if (video) this.#hydrate(video);
      }
    }, { rootMargin: '200px', threshold: 0 });
    this.querySelectorAll('.echt-community-reels__card').forEach((card) => this.#nearby.observe(card));
    const sync = () => {
      for (const [video, state] of this.#states) {
        if (document.hidden || this.#motion.matches || navigator.connection?.saveData) video.pause();
        else this.#autoplay(video, state);
      }
    };
    document.addEventListener('visibilitychange', sync, { signal });
    this.#motion.addEventListener('change', sync, { signal });
    navigator.connection?.addEventListener('change', sync, { signal });
    this.#visibility?.disconnect();
    this.#visibility = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const video = entry.target.querySelector('video');
        const state = this.#states.get(video);
        if (!state) continue;
        state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        if (!state.visible) video.pause();
        else this.#autoplay(video, state);
      }
    }, { threshold: [0, 0.5] });
    this.querySelectorAll('.echt-community-reels__card').forEach((card) => this.#visibility.observe(card));
  }

  #bindNavigation(signal) {
    this.#rail = this.querySelector('.echt-community-reels__rail');
    this.#navigation = this.querySelector('.echt-community-reels__navigation');
    this.#previous = this.querySelector('[data-reel-direction="previous"]');
    this.#next = this.querySelector('[data-reel-direction="next"]');
    if (!this.#rail || !this.#navigation || !this.#previous || !this.#next) return;
    this.#previous.addEventListener('click', () => this.#move(-1), { signal });
    this.#next.addEventListener('click', () => this.#move(1), { signal });
    this.#rail.addEventListener('scroll', () => this.#updateNavigation(), { signal, passive: true });
    this.#resize?.disconnect();
    this.#resize = new ResizeObserver(() => this.#updateNavigation());
    this.#resize.observe(this.#rail);
    this.querySelectorAll('.echt-community-reels__card').forEach((card) => this.#resize.observe(card));
    this.#updateNavigation();
  }

  #move(direction) {
    const first = this.querySelector('.echt-community-reels__card');
    if (!first || !this.#rail) return;
    const gap = parseFloat(getComputedStyle(first.parentElement).columnGap) || 0;
    const sign = getComputedStyle(this.#rail).direction === 'rtl' ? -1 : 1;
    this.#rail.scrollBy({
      left: direction * sign * (first.getBoundingClientRect().width + gap),
      behavior: this.#motion.matches ? 'instant' : 'smooth',
    });
  }

  #updateNavigation() {
    if (!this.#rail || !this.#navigation || !this.#previous || !this.#next) return;
    const maxScroll = this.#rail.scrollWidth - this.#rail.clientWidth;
    const position = Math.abs(this.#rail.scrollLeft);
    this.#navigation.hidden = maxScroll <= 1;
    this.#previous.disabled = position <= 1;
    this.#next.disabled = position >= maxScroll - 1;
  }

  #hydrate(video, sources = false) {
    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      delete video.dataset.poster;
    }
    if (!sources) return;
    let changed = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      delete source.dataset.src;
      changed = true;
    });
    if (changed) video.load();
  }

  #bindVideo(video, signal) {
    const state = this.#states.get(video) || { manualPaused: false, visible: false, failed: false };
    this.#states.set(video, state);
    video.parentElement.querySelector('.echt-community-reels__controls')?.remove();
    const controls = document.createElement('div');
    controls.className = 'echt-community-reels__controls';
    const play = document.createElement('button');
    const mute = document.createElement('button');
    for (const button of [play, mute]) {
      button.type = 'button';
      button.className = 'echt-community-reels__control';
      controls.append(button);
    }
    const icon = (path) => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${path}"/></svg>`;
    const refresh = () => {
      const title = video.getAttribute('aria-label') || 'Community film';
      play.setAttribute('aria-label', `${video.paused ? 'Play' : 'Pause'} ${title}`);
      play.innerHTML = icon(video.paused ? 'M8 5 19 12 8 19Z' : 'M8 5V19M16 5V19');
      mute.setAttribute('aria-label', `${video.muted ? 'Unmute' : 'Mute'} ${title}`);
      mute.innerHTML = icon(video.muted ? 'M11 5 6 9H3V15H6L11 19ZM16 9 22 15M22 9 16 15' : 'M11 5 6 9H3V15H6L11 19ZM15 8Q20 12 15 16M18 5Q25 12 18 19');
    };
    play.addEventListener('click', () => {
      if (video.paused) {
        state.manualPaused = false;
        this.#hydrate(video, true);
        video.play().catch(() => refresh());
      } else {
        state.manualPaused = true;
        video.pause();
      }
    }, { signal });
    mute.addEventListener('click', () => {
      video.muted = !video.muted;
    }, { signal });
    video.addEventListener('volumechange', () => {
      if (!video.muted) this.querySelectorAll('video').forEach((other) => { if (other !== video) other.muted = true; });
      refresh();
    }, { signal });
    for (const event of ['play', 'pause', 'ended']) video.addEventListener(event, refresh, { signal });
    video.addEventListener('error', () => {
      state.failed = true;
      video.controls = true;
      controls.hidden = true;
    }, { signal });
    video.muted = true;
    video.controls = false;
    video.hidden = false;
    video.parentElement.append(controls);
    refresh();
  }

  #autoplay(video, state) {
    if (!this.isConnected || !state.visible || state.manualPaused || state.failed || document.hidden || this.#motion.matches || navigator.connection?.saveData) return;
    if (!video.paused) return;
    this.#hydrate(video, true);
    video.muted = true;
    video.play().catch(() => { /* Manual play remains available if autoplay is blocked. */ });
  }

  #pause() {
    this.querySelectorAll('video').forEach((video) => video.pause());
  }

  disconnectedCallback() {
    this.#pause();
    this.#abort?.abort();
    this.#visibility?.disconnect();
    this.#resize?.disconnect();
    this.#nearby?.disconnect();
    if (this.#navigation) this.#navigation.hidden = true;
    this.querySelectorAll('video').forEach((video) => { video.controls = true; });
    this.querySelectorAll('.echt-community-reels__controls').forEach((controls) => controls.remove());
    for (const state of this.#states.values()) state.visible = false;
    super.disconnectedCallback();
  }
}

if (!customElements.get('echt-community-reels')) {
  customElements.define('echt-community-reels', EchtCommunityReels);
}
